import React, { FC, ReactText, useCallback, useMemo, useRef } from 'react';
import { Alert, Dropdown, MenuProps } from 'antd';
import { Store } from 'antd/lib/form/interface';
import { PaginationConfig } from 'antd/lib/pagination';
import { SorterResult, TableCurrentDataSource } from 'antd/lib/table/interface';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  OptionProps,
  ReportKey,
  ReportsFilters,
  StructuralPassFailReportResponse,
  StructuralPassFailRow,
} from '@models/interfaces';
import { CustomTable } from '@shared/components';
import { PrimaryButton } from '@shared/modules';
import type {
  ReportsSummaryMetric,
  StructuralAnalysisResultsChartItem,
  StructuralGroupSeriesChartItem,
  StructuralPassFailGroupKey,
} from '../../models';
import {
  formatStructuralPassRate,
  FULL_FILTER_CAPTION,
  getStructuralAnalysisResultsChartData,
  getStructuralGroupSeriesChartData,
  getStructuralPassFailColumns,
  getStructuralPassFailGroupTitle,
  getStructuralPassFailReportRowClassName,
  getStructuralPassFailReportRowKey,
  getStructuralPassFailRowRedirectUrl,
  getStructuralPassRate,
  getStructuralPassRateGaugeData,
  getStructuralStatusColor,
  isInteractiveRowClick,
  openReportRowInNewTab,
  STRUCTURAL_PASS_FAIL_GROUP_KEYS,
  STRUCTURAL_STATUS_LABELS,
} from '../../models';
import { useReportExport } from '../../models/use-report-export';
import { ReportChartCard } from '../report-chart-card';
import { ReportsSummary } from '../reports-summary';

import './styles.scss';
import themeColors from 'styles/theme/colors.module.scss';

interface StructuralPassFailReportProps {
  data?: StructuralPassFailReportResponse;
  filters: ReportsFilters;
  onTableChange: (
    pagination: PaginationConfig,
    tableFilters: Record<string, ReactText[] | null>,
    sorter: SorterResult<Store> | SorterResult<Store>[],
    extra: TableCurrentDataSource<Store>,
  ) => void;
  sortByOptions: OptionProps[];
}

export const StructuralPassFailReport: FC<StructuralPassFailReportProps> = ({
  data,
  filters,
  onTableChange,
  sortByOptions,
}: StructuralPassFailReportProps) => {
  const chartsRef = useRef<HTMLDivElement>(null);
  const rows = data?.table?.items ?? [];
  const total = data?.table?.total ?? 0;
  const analysisResults = useMemo(
    () => getStructuralAnalysisResultsChartData(data?.summary),
    [data?.summary],
  );
  const groupSeries = useMemo(
    () => getStructuralGroupSeriesChartData(data?.charts?.groupSeries),
    [data?.charts?.groupSeries],
  );
  const passRate = getStructuralPassRate(data?.summary);
  const passRateGaugeData = useMemo(
    () => getStructuralPassRateGaugeData(passRate),
    [passRate],
  );
  const groupTitle = getStructuralPassFailGroupTitle(filters.groupBy);
  const columns = useMemo(
    () => getStructuralPassFailColumns(filters, sortByOptions),
    [filters, sortByOptions],
  );
  const metrics: ReportsSummaryMetric[] = [
    {
      caption: FULL_FILTER_CAPTION,
      label: 'Pass',
      value: data?.summary?.pass ?? 0,
    },
    {
      caption: FULL_FILTER_CAPTION,
      label: 'Fail',
      value: data?.summary?.fail ?? 0,
    },
    {
      caption: FULL_FILTER_CAPTION,
      label: 'Pending',
      value: data?.summary?.pending ?? 0,
    },
    {
      caption: FULL_FILTER_CAPTION,
      label: 'Total',
      value: data?.summary?.total ?? 0,
    },
    {
      caption: FULL_FILTER_CAPTION,
      label: 'Pass Rate',
      value: formatStructuralPassRate(passRate),
    },
  ];

  const onRowClick = useCallback(
    (
      record: StructuralPassFailRow,
      _newTab: boolean,
      event: { target?: EventTarget | null },
    ): void => {
      const redirectUrl = getStructuralPassFailRowRedirectUrl(record);

      if (!redirectUrl || isInteractiveRowClick(event)) {
        return;
      }

      openReportRowInNewTab(redirectUrl);
    },
    [],
  );

  const { exporting, exportError, onExportExcel, onExportPdf } =
    useReportExport({
      columns,
      metrics,
      rows,
      chartsRef,
      reportTitle: 'Structural Pass Fail',
      scope: {
        page: filters.page ?? 1,
        size: filters.size ?? 25,
        total,
        filters,
      },
    });

  const exportMenu: MenuProps = {
    items: [
      { key: 'excel', label: 'Export as Excel', onClick: onExportExcel },
      { key: 'pdf', label: 'Export as PDF', onClick: onExportPdf },
    ],
  };

  return (
    <div className="prov-structural-pass-fail-report">
      <div className="prov-structural-pass-fail-report__toolbar">
        <p className="prov-structural-pass-fail-report__description">
          Pass/fail/pending for projects with a Structural Ordered date.
        </p>
        <Dropdown
          disabled={!rows.length || exporting}
          menu={exportMenu}
          trigger={['click']}
        >
          <PrimaryButton
            className="prov-structural-pass-fail-report__export"
            title={exporting ? 'Exporting…' : 'Export current page ▾'}
          />
        </Dropdown>
      </div>

      {exportError && <Alert type="error" message={exportError} />}
      <ReportsSummary metrics={metrics} />

      <div className="prov-structural-pass-fail-report__charts" ref={chartsRef}>
        <ReportChartCard
          hasData={analysisResults.some(
            ({ count }: StructuralAnalysisResultsChartItem) => count > 0,
          )}
          title="Structural Analysis Results"
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={analysisResults}
              layout="vertical"
              margin={{ bottom: 8, left: 8, right: 24, top: 8 }}
            >
              <CartesianGrid stroke={themeColors.chartGrid} />
              <XAxis
                allowDecimals={false}
                stroke={themeColors.fontColor}
                type="number"
              />
              <YAxis
                dataKey="displayLabel"
                stroke={themeColors.fontColor}
                type="category"
                width={104}
              />
              <Tooltip />
              <Bar dataKey="count" name="Projects">
                {analysisResults.map(
                  ({ status }: StructuralAnalysisResultsChartItem) => (
                    <Cell
                      fill={getStructuralStatusColor(status)}
                      key={status}
                    />
                  ),
                )}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ReportChartCard>

        <ReportChartCard
          hasData={(data?.summary?.total ?? 0) > 0}
          title="Pass Rate"
        >
          <div className="prov-structural-pass-fail-report__pass-rate-gauge">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  cx="50%"
                  cy="76%"
                  data={passRateGaugeData}
                  dataKey="value"
                  endAngle={0}
                  innerRadius="72%"
                  isAnimationActive={false}
                  outerRadius="100%"
                  startAngle={180}
                  stroke="none"
                >
                  {passRateGaugeData.map(({ fill, name }) => (
                    <Cell fill={fill} key={name} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="prov-structural-pass-fail-report__pass-rate-label">
              <strong>{formatStructuralPassRate(passRate)}</strong>
              <span>Pass Rate</span>
            </div>
          </div>
        </ReportChartCard>

        <ReportChartCard
          hasData={groupSeries.some(
            ({ fail, pass }: StructuralGroupSeriesChartItem) => pass + fail > 0,
          )}
          title={groupTitle}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={groupSeries}
              layout="vertical"
              margin={{ bottom: 8, left: 8, right: 24, top: 8 }}
            >
              <CartesianGrid stroke={themeColors.chartGrid} />
              <XAxis
                allowDecimals={false}
                stroke={themeColors.fontColor}
                type="number"
              />
              <YAxis
                dataKey="groupLabel"
                stroke={themeColors.fontColor}
                type="category"
                width={132}
              />
              <Tooltip />
              <Legend />
              {STRUCTURAL_PASS_FAIL_GROUP_KEYS.map(
                (status: StructuralPassFailGroupKey) => (
                  <Bar
                    dataKey={status}
                    fill={getStructuralStatusColor(status)}
                    key={status}
                    name={STRUCTURAL_STATUS_LABELS[status]}
                    stackId="structural-status"
                  />
                ),
              )}
            </BarChart>
          </ResponsiveContainer>
        </ReportChartCard>
      </div>

      <CustomTable
        module="reports"
        subModule={ReportKey.StructuralPassFail}
        className="prov-structural-pass-fail-report__table"
        columns={columns}
        dataSource={rows}
        onChange={onTableChange}
        onRowClick={onRowClick}
        pagination={{
          current: filters.page,
          pageSize: filters.size,
          total,
        }}
        rowClassName={getStructuralPassFailReportRowClassName}
        rowKey={getStructuralPassFailReportRowKey}
        scroll={{ x: 3200, y: 'auto' }}
      />
    </div>
  );
};
